import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { rosterResponseSchema, unitsResponseSchema } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { ApiError } from '../../components/ApiError.js';
import { useClaimRoles } from '../auth/useClaimRoles.js';
import { CreateSoldierForm } from '../users/CreateSoldierForm.js';
import { PLATOON_LEADER_ROLES, STOREROOM_ROLES } from '../../components/RequireRole.js';
import { personName } from '@unit/shared';

/**
 * One unit's roster (REQUIREMENTS §2.6, "unit tree with soldier list").
 *
 * Served by a Cloud Function rather than read from Firestore directly: scoping
 * a roster to the caller's chain of command needs a query the client cannot be
 * trusted to constrain, and the `users` rules deliberately allow no open list.
 */
export function UnitRosterPage() {
  const { t } = useTranslation();
  const { unitId } = useParams<{ unitId: string }>();

  const units = useApi('/units', unitsResponseSchema);
  const roster = useApi(unitId ? `/units/${unitId}/soldiers` : null, rosterResponseSchema);
  const { roles } = useClaimRoles();
  const unit = units.kind === 'ready' ? units.data.units.find((u) => u.id === unitId) : undefined;
  // Soldiers are created into a *team*; a platoon page lists but does not add.
  const canAdd = roles.includes('admin') && unit?.type === 'team' && unit.active;
  // Bulk issue (§3.5): storeroom roles and platoon commanders. The server
  // re-checks that the unit is within the caller's reach.
  const canBulk = roles.some((r) => STOREROOM_ROLES.includes(r) || PLATOON_LEADER_ROLES.includes(r)) && unit?.active;

  // The id is a fallback for a unit missing from the structure, not a thing to
  // show while the structure is still loading — flashing "team-2a" and then
  // replacing it with "צוות 2א" reads as a rendering bug to anyone watching.
  const unitName =
    units.kind === 'ready'
      ? (units.data.units.find((u) => u.id === unitId)?.name ?? unitId)
      : null;

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900" data-testid="unit-name">
          {unitName ?? <span className="text-slate-400">{t('home.loading')}</span>}
        </h2>
        <span className="flex items-center gap-3">
          {canBulk && unitId && (
            <Link
              to={`/issue/bulk?unit=${unitId}`}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700"
              data-testid="bulk-issue"
            >
              {t('bulk.button')}
            </Link>
          )}
          <Link to="/org" className="text-sm text-slate-500 underline-offset-4 hover:underline">
            {t('org.backToTree')}
          </Link>
        </span>
      </div>

      {roster.kind === 'loading' && <p className="mt-3 text-slate-500">{t('home.loading')}</p>}
      {roster.kind === 'error' && (
        <div className="mt-3">
          <ApiError code={roster.code} requestId={roster.requestId} />
        </div>
      )}

      {roster.kind === 'ready' && (
        <>
          <p className="mt-1 text-sm text-slate-500">
            {t('org.soldierCount', { count: roster.data.soldiers.length })}
          </p>

          {/* Horizontally scrollable on its own, so a narrow phone never makes
              the whole page scroll sideways. */}
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-start text-sm" data-testid="roster">
              <thead>
                <tr className="border-b border-slate-200 text-xs text-slate-500">
                  <th scope="col" className="py-2 text-start font-medium">
                    {t('org.columns.name')}
                  </th>
                  <th scope="col" className="py-2 text-start font-medium">
                    {t('users.rank')}
                  </th>
                  <th scope="col" className="py-2 text-start font-medium">
                    {t('org.columns.personalNumber')}
                  </th>
                  <th scope="col" className="py-2 text-start font-medium">
                    {t('org.columns.title')}
                  </th>
                  <th scope="col" className="py-2 text-start font-medium">
                    {t('org.columns.roles')}
                  </th>
                  <th scope="col" className="py-2 text-end font-medium">
                    {t('org.columns.gear')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {roster.data.soldiers.map((soldier) => (
                  <tr key={soldier.uid}>
                    <td className="py-2 font-medium text-slate-900">
                      <Link
                        to={`/org/${unitId}/soldier/${soldier.uid}`}
                        className="underline-offset-4 hover:underline"
                      >
                        {personName(soldier)}
                      </Link>
                    </td>
                    <td className="py-2 text-slate-600">{soldier.rank ?? '—'}</td>
                    {/* The isolation goes on a span *inside* the cell, never on
                        the cell. `direction: ltr` on a table-cell re-aligns the
                        whole cell, which in RTL pushes the number against the
                        next column and renders "9100027נהג" as one run. */}
                    <td className="py-2 text-start text-slate-600">
                      <span className="latin-inline">{soldier.personalNumber}</span>
                    </td>
                    <td className="py-2 text-slate-600">{soldier.title ?? '—'}</td>
                    <td className="py-2 text-slate-600">
                      {soldier.roles.map((role) => t(`roles.${role}`)).join(' · ')}
                    </td>
                    <td className="py-2 text-end text-slate-700" data-testid="gear-count">
                      <span className="latin-inline">{roster.data.openGear[soldier.uid] ?? 0}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {roster.data.soldiers.length === 0 && (
            <p className="mt-3 text-slate-500">{t('org.noSoldiers')}</p>
          )}

          {canAdd && unitId && (
            <div className="mt-5">
              <CreateSoldierForm unitId={unitId} onCreated={() => roster.reload()} />
            </div>
          )}
        </>
      )}
    </section>
  );
}
